import React, { useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { NumberSelection } from "../../../../../components/number-selection/NumberSelection";
import { SubscriptionPlan } from "../../../../../services/organization-subscription-plans/SubscriptionPlan";
import { translationService } from "../../../../../services/translation/TranslationService";
import { InfoTooltip } from "../info-tooltip/InfoTooltip";
import "./composition.scss";

interface PackagePricingPerSeatsProps {
  subscriptionPlan: SubscriptionPlan;
  baseSubscriptionPlan: SubscriptionPlan;
  totalSeatsAmountChanged: (newAmount: number) => void;
  isActiveInNextBillingPeriod: boolean;
  isCurrentlyActive: boolean;
  currentSubscriptionWillBeRenewed: boolean;
  getLocalizedPrice: (price: number) => string;
  nextBillingDate?: Date;
}

export const PackagePricingPerSeats = observer((props: PackagePricingPerSeatsProps) => {
  const MAX_NUMBER_OF_SEATS = 9999;
  const {
    subscriptionPlan,
    baseSubscriptionPlan,
    totalSeatsAmountChanged,
    isActiveInNextBillingPeriod,
    isCurrentlyActive,
    currentSubscriptionWillBeRenewed,
    nextBillingDate,
    getLocalizedPrice,
  } = props;

  const [seatsAmount, setSeatsAmount] = useState(
    subscriptionPlan.activeSubscriptionPackageAmount ||
      baseSubscriptionPlan.previouslySelectedAmount ||
      baseSubscriptionPlan.minimumNumberOfSeats
  );
  const [totalSeatsPrice, setTotalSeatsPrice] = useState(
    subscriptionPlan.activeSubscriptionPackageAmount
      ? subscriptionPlan.activeSubscriptionPackageAmount * baseSubscriptionPlan.priceAmount
      : baseSubscriptionPlan.previouslySelectedAmount
      ? baseSubscriptionPlan.previouslySelectedAmount * baseSubscriptionPlan.priceAmount
      : baseSubscriptionPlan.minimumNumberOfSeats * baseSubscriptionPlan.priceAmount
  );

  const [totalSewerInspectionModulePrice, setTotalSewerInspectionModulePrice] = useState(
    subscriptionPlan.activeSubscriptionPackageAmount
      ? subscriptionPlan.activeSubscriptionPackageAmount *
          baseSubscriptionPlan.sewerInspectionModule!.priceAmount
      : baseSubscriptionPlan.previouslySelectedAmount
      ? baseSubscriptionPlan.previouslySelectedAmount *
        baseSubscriptionPlan.sewerInspectionModule!.priceAmount
      : baseSubscriptionPlan.minimumNumberOfSeats *
        baseSubscriptionPlan.sewerInspectionModule!.priceAmount
  );

  const seatsAmountChanged = useCallback(
    (newAmount: number) => {
      setSeatsAmount(newAmount);
      setTotalSeatsPrice(baseSubscriptionPlan.priceAmount * newAmount);
      setTotalSewerInspectionModulePrice(
        baseSubscriptionPlan.sewerInspectionModule!.priceAmount * newAmount
      );
    },
    [baseSubscriptionPlan.priceAmount, baseSubscriptionPlan.sewerInspectionModule]
  );

  useEffect(() => {
    totalSeatsAmountChanged(seatsAmount);
  }, [seatsAmount, totalSeatsAmountChanged]);

  useEffect(() => {
    if (baseSubscriptionPlan.previouslySelectedAmount) {
      setSeatsAmount(baseSubscriptionPlan.previouslySelectedAmount);
      setTotalSeatsPrice(
        baseSubscriptionPlan.previouslySelectedAmount * baseSubscriptionPlan.priceAmount
      );
      setTotalSewerInspectionModulePrice(
        baseSubscriptionPlan.previouslySelectedAmount *
          baseSubscriptionPlan.sewerInspectionModule!.priceAmount
      );
    }
  }, [
    baseSubscriptionPlan.priceAmount,
    baseSubscriptionPlan.previouslySelectedAmount,
    baseSubscriptionPlan.sewerInspectionModule,
  ]);

  return (
    <div className="package-pricing-container">
      <div className="medium-label">
        {translationService.translate("SubscriptionPlans.card.package-price")}
      </div>
      <div className="number-selection-container">
        <NumberSelection
          initialValue={
            subscriptionPlan.activeSubscriptionPackageAmount ||
            baseSubscriptionPlan.previouslySelectedAmount ||
            baseSubscriptionPlan.minimumNumberOfSeats
          }
          minValue={baseSubscriptionPlan.minimumNumberOfSeats}
          maxValue={MAX_NUMBER_OF_SEATS}
          labelTranslationKey="SubscriptionPlans.card.seats-general"
          callback={seatsAmountChanged}
          minusButtonDisabled={seatsAmount <= baseSubscriptionPlan.minimumNumberOfSeats}
          plusButtonDisabled={seatsAmount >= MAX_NUMBER_OF_SEATS}
        />
        {isActiveInNextBillingPeriod && currentSubscriptionWillBeRenewed && nextBillingDate && (
          <InfoTooltip
            nextBillingDate={nextBillingDate}
            firstBoldedTextKey="SubscriptionPlans.card.info.adding"
            firstTextKey="SubscriptionPlans.card.seats.info.adding"
            secondBoldedTextKey="SubscriptionPlans.card.info.removing"
            secondTextKey="SubscriptionPlans.card.seats.info.removing"
          />
        )}
        {isActiveInNextBillingPeriod && !currentSubscriptionWillBeRenewed && nextBillingDate && (
          <InfoTooltip
            nextBillingDate={nextBillingDate}
            firstBoldedTextKey="SubscriptionPlans.card.info.adding-or-removing"
            firstTextKey="SubscriptionPlans.card.seats.downgrade.info"
          />
        )}
        {!isActiveInNextBillingPeriod && isCurrentlyActive && nextBillingDate && (
          <InfoTooltip
            nextBillingDate={nextBillingDate}
            firstBoldedTextKey="SubscriptionPlans.card.info.adding"
            firstTextKey="SubscriptionPlans.card.seats.info.adding"
            secondBoldedTextKey="SubscriptionPlans.card.info.removing"
            secondTextKey="SubscriptionPlans.card.seats.info.removing"
          />
        )}
      </div>
      <div>
        <span className="large-label">
          {`${getLocalizedPrice(totalSeatsPrice + totalSewerInspectionModulePrice)} €`}
        </span>
      </div>
      <div>
        <span className="seats-total-price">{`(${getLocalizedPrice(
          baseSubscriptionPlan.priceAmount + baseSubscriptionPlan.sewerInspectionModule!.priceAmount
        )} € x ${seatsAmount}
        ${translationService.translate("SubscriptionPlans.card.seats-billing-cycle")}) *`}</span>
      </div>
      <div className="sewer-inspection-module-price">
        <div className="small-opacity-label align-left">
          {`* ${translationService.translate("SubscriptionPlans.card.seats-bm-module-price", {
            price: getLocalizedPrice(
              baseSubscriptionPlan.sewerInspectionModule!.priceAmount * seatsAmount
            ),
          })}`}
        </div>
      </div>
    </div>
  );
});
